import React from 'react';
import { List, Datagrid, DateField, TextField, BooleanField, FunctionField, Button, TopToolbar, ReferenceInput, SelectInput, Filter, TextInput, useDataProvider, useNotify, downloadCSV } from 'react-admin';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import MuiTextField from '@mui/material/TextField';
import MuiTooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell, Legend } from 'recharts';
import jsonExport from 'jsonexport/dist';

import { apiUrl, araTiquetsUrl } from '../common/constants';
import { useTranslate } from '../common/language';
import { headersAPIDocAuth } from '../common/utils';
import { CreateEventQuickForm } from './events';
import { EventsSessionsDetails } from './events_sessions';
import { TicketsMapSvgPreview } from '../components/TicketsMaps';
import { validateMaxLength } from '../components/Validator';

const percentLabel = value => `${Number(value || 0).toFixed(1)}%`;
const amountLabel = value => Number(value || 0).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR';
const valueLabel = (value, mode) => mode === 'percentage' ? percentLabel(value) : Number(value || 0).toLocaleString('ca-ES');
const dateTimeLabel = value => value ? new Date(value).toLocaleString('ca-ES') : '';
const attendanceLabel = row => row && (row.validated_at || Number(row.num_validations || 0) > 0) ? 'Sí' : 'No';
const originLabel = row => row && row.is_box_office ? 'Guixeta' : 'En línia';
const ticketCount = (booking, tickets) => tickets.filter(ticket => Number(ticket.tickets_booking_id) === Number(booking.id)).length;
const statusLabel = value => value === null || value === undefined ? '' : value;
const ticketPdfUrl = row => row && row.id ? `${apiUrl}/tickets_booking_items/${row.id}/pdf` : null;
const bookingPdfUrl = row => row && row.id ? `${apiUrl}/tickets_booking/${row.id}/pdf` : null;
const sessionTitle = record => record ? [record.event_name, dateTimeLabel(record.date_start)].filter(Boolean).join(' - ') : '';

const downloadPrivatePdf = async (event, url, filename, notify) => {
    event.stopPropagation();
    if (!url) return;

    try {
        const response = await fetch(url, {
            headers: {
                ...headersAPIDocAuth(),
                Accept: 'application/pdf'
            }
        });

        if (!response.ok) {
            throw new Error(`PDF download failed: ${response.status}`);
        }

        const blob = await response.blob();
        const href = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(href);
    } catch (error) {
        console.error(error);
        notify("No s'ha pogut descarregar el PDF", 'warning');
    }
};

const Segment = ({ value, color }) => (
    <Box
        sx={{
            width: `${Math.max(0, Number(value || 0))}%`,
            backgroundColor: color,
            height: 10
        }}
    />
);

const SalesReportField = ({ record, subscriptions = false }) => {
    const translate = useTranslate();
    if (!record) return null;

    const total = Number(record.map_status_total || 0);
    const statusPercentage = value => total > 0 ? (Number(value || 0) * 100) / total : 0;

    const stats = [
        {
            key: 'purchased',
            label: translate('tickets_maps.status_purchased'),
            value: record.map_status_purchased,
            color: '#000000'
        },
        {
            key: 'invitation',
            label: translate('tickets_maps.status_invitation'),
            value: record.map_status_invitation,
            color: '#4b5563'
        },
        {
            key: 'reserved',
            label: translate('tickets_maps.status_reserved'),
            value: record.map_status_reserved,
            color: '#d1d5db',
            hidden: subscriptions
        },
        {
            key: 'pending_payment',
            label: translate('tickets_maps.status_pending_payment'),
            value: record.map_status_pending_payment,
            color: '#facc15'
        },
        {
            key: 'basket',
            label: translate('tickets_maps.status_basket'),
            value: record.map_status_basket,
            color: '#3b82f6'
        },
        {
            key: 'available_only_box_office',
            label: translate('tickets_maps.status_available_only_box_office'),
            value: record.map_status_available_only_box_office,
            color: '#f59e0b',
            hidden: subscriptions
        },
        {
            key: 'available',
            label: translate('tickets_maps.status_available_everyone'),
            value: record.map_status_available,
            color: '#22c55e'
        }
    ].filter(item => !item.hidden).map(item => ({ ...item, percentage: statusPercentage(item.value) }));
    const barLabel = stats
        .map(item => `${item.label}: ${item.value || 0} (${percentLabel(item.percentage)})`)
        .join(', ');

    return (
        <Box sx={{ minWidth: 320, maxWidth: 460 }}>
            <MuiTooltip
                arrow
                placement="top"
                title={(
                    <Box sx={{ py: 0.5 }}>
                        {stats.map(item => (
                            <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}>
                                <Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: item.color, border: '1px solid rgba(255,255,255,0.45)' }} />
                                <span>{item.label}: {item.value || 0} ({percentLabel(item.percentage)})</span>
                            </Box>
                        ))}
                    </Box>
                )}
            >
                <Box
                    aria-label={barLabel}
                    sx={{ display: 'flex', width: '100%', overflow: 'hidden', borderRadius: 1, backgroundColor: '#fafafa' }}
                >
                    {stats.map(item => (
                        <Segment
                            key={item.key}
                            value={item.percentage}
                            color={item.color}
                        />
                    ))}
                </Box>
            </MuiTooltip>
        </Box>
    );
};

const IncomeTotalField = ({ record, subscriptions = false }) => {
    const translate = useTranslate();
    if (!record) return null;

    const ticketBase = Number(record.income_ticket_base || 0);
    const ticketVat = Number(record.income_ticket_vat || 0);
    const managementBase = Number(record.income_management_base || 0);
    const managementVat = Number(record.income_management_vat || 0);
    const ticketSubtotal = ticketBase + ticketVat;
    const managementSubtotal = managementBase + managementVat;

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
            <span>{amountLabel(record.income_total)}</span>
            <MuiTooltip
                arrow
                placement="top"
                title={(
                    <Box sx={{ py: 0.5, minWidth: 230 }}>
                        <div>{translate(subscriptions ? 'ara_tiquets_admin.income_subscription_base' : 'ara_tiquets_admin.income_ticket_base')}: {amountLabel(ticketBase)}</div>
                        <div>{translate(subscriptions ? 'ara_tiquets_admin.income_subscription_vat' : 'ara_tiquets_admin.income_ticket_vat')}: {amountLabel(ticketVat)}</div>
                        <div><strong>{translate(subscriptions ? 'ara_tiquets_admin.income_subscription_subtotal' : 'ara_tiquets_admin.income_ticket_subtotal')}: {amountLabel(ticketSubtotal)}</strong></div>
                        <Box sx={{ height: 6 }} />
                        <div>{translate('ara_tiquets_admin.income_management_base')}: {amountLabel(managementBase)}</div>
                        <div>{translate('ara_tiquets_admin.income_management_vat')}: {amountLabel(managementVat)}</div>
                        <div><strong>{translate('ara_tiquets_admin.income_management_subtotal')}: {amountLabel(managementSubtotal)}</strong></div>
                        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.35)' }}>
                            <strong>{translate('ara_tiquets_admin.income_total')}: {amountLabel(record.income_total)}</strong>
                        </Box>
                    </Box>
                )}
            >
                <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </MuiTooltip>
        </Box>
    );
};

const PurchasePriceField = ({ record, subscriptions = false, totalSource = 'amount_total' }) => {
    const translate = useTranslate();
    if (!record) return null;

    const itemBase = Number(record.price_net || 0);
    const itemVat = Number(subscriptions ? record.tax_subscription_fee || 0 : record.tax_event_amount || 0);
    const managementBase = Number(record.price_service_fee || 0);
    const managementVat = Number(record.tax_service_amount || 0);
    const itemSubtotal = itemBase + itemVat;
    const managementSubtotal = managementBase + managementVat;
    const total = record[totalSource];

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, whiteSpace: 'nowrap' }}>
            <span>{amountLabel(total)}</span>
            <MuiTooltip
                arrow
                placement="top"
                title={(
                    <Box sx={{ py: 0.5, minWidth: 230 }}>
                        <div>{translate(subscriptions ? 'ara_tiquets_admin.income_subscription_base' : 'ara_tiquets_admin.income_ticket_base')}: {amountLabel(itemBase)}</div>
                        <div>{translate(subscriptions ? 'ara_tiquets_admin.income_subscription_vat' : 'ara_tiquets_admin.income_ticket_vat')}: {amountLabel(itemVat)}</div>
                        <div><strong>{translate(subscriptions ? 'ara_tiquets_admin.income_subscription_subtotal' : 'ara_tiquets_admin.income_ticket_subtotal')}: {amountLabel(itemSubtotal)}</strong></div>
                        <Box sx={{ height: 6 }} />
                        <div>{translate('ara_tiquets_admin.income_management_base')}: {amountLabel(managementBase)}</div>
                        <div>{translate('ara_tiquets_admin.income_management_vat')}: {amountLabel(managementVat)}</div>
                        <div><strong>{translate('ara_tiquets_admin.income_management_subtotal')}: {amountLabel(managementSubtotal)}</strong></div>
                        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.35)' }}>
                            <strong>{translate('ara_tiquets_admin.income_total')}: {amountLabel(total)}</strong>
                        </Box>
                    </Box>
                )}
            >
                <IconButton
                    size="small"
                    aria-label={translate('ara_tiquets_admin.price_breakdown')}
                    sx={{ p: 0.25, color: 'text.secondary' }}
                >
                    <InfoOutlinedIcon fontSize="small" />
                </IconButton>
            </MuiTooltip>
        </Box>
    );
};

const chartTooltip = (mode, translate) => ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
        <Box sx={{ backgroundColor: '#fff', border: '1px solid #ddd', p: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>{label}</Typography>
            {payload.map(item => (
                <Typography key={item.dataKey} variant="body2" sx={{ fontWeight: 600, color: item.color }}>
                    {item.name || translate('ara_tiquets_admin.' + item.dataKey)}: {valueLabel(item.value, mode)}
                </Typography>
            ))}
        </Box>
    );
};

const chartData = (items, mode, translate) => (items || []).map(item => ({
    ...item,
    source_label: item.label,
    label: item.label === 'attended' || item.label === 'not_attended' ? translate('ara_tiquets_admin.' + item.label) : item.label,
    chart_value: mode === 'percentage' ? item.percentage : item.value,
    online_value: mode === 'percentage' ? item.online_percentage : item.online,
    box_office_value: mode === 'percentage' ? item.box_office_percentage : item.box_office,
    invitations_value: mode === 'percentage' ? item.invitations_percentage : item.invitations
}));

const AdminSessionExpand = ({ record }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [mode, setMode] = React.useState('absolute');
    const [details, setDetails] = React.useState(null);
    const [loadingDetails, setLoadingDetails] = React.useState(true);
    const salesData = chartData(details?.sales_accumulated, mode, translate);
    const attendanceData = chartData(details?.attendance, mode, translate);
    const sessionRecord = {
        ...record,
        name: record.session_name,
        total_capacity: record.total_on_sale
    };

    React.useEffect(() => {
        const controller = new AbortController();
        setLoadingDetails(true);
        setDetails(null);
        fetch(`${apiUrl}/ara-tiquets/admin/sessions/${record.id}/details`, {
            headers: headersAPIDocAuth(),
            signal: controller.signal
        })
            .then(response => {
                if (!response.ok) throw new Error(`Session details failed: ${response.status}`);
                return response.json();
            })
            .then(data => setDetails(data))
            .catch(error => {
                if (error.name !== 'AbortError') notify('ra.notification.http_error', { type: 'warning' });
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoadingDetails(false);
            });
        return () => controller.abort();
    }, [record.id, notify]);

    return (
        <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
            <EventsSessionsDetails record={sessionRecord} ticketing />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, mb: 1 }}>
                <Button
                    onClick={() => setMode('absolute')}
                    label="ara_tiquets_admin.absolute_values"
                    disabled={mode === 'absolute'}
                />
                <Button
                    onClick={() => setMode('percentage')}
                    label="ara_tiquets_admin.percentage_values"
                    disabled={mode === 'percentage'}
                />
            </Box>

            {loadingDetails ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : (
                <>

            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{translate('ara_tiquets_admin.sales_accumulated')}</Typography>
                <Box sx={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={salesData} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" />
                            <YAxis tickFormatter={value => mode === 'percentage' ? `${value}%` : value} />
                            <Tooltip content={chartTooltip(mode, translate)} />
                            <Legend />
                            <Line name={translate('ara_tiquets_admin.sold_total')} type="monotone" dataKey="chart_value" stroke="#263238" strokeWidth={3} dot={{ r: 3 }} />
                            <Line name={translate('ara_tiquets_admin.sold_online')} type="monotone" dataKey="online_value" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }} />
                            <Line name={translate('ara_tiquets_admin.sold_box_office')} type="monotone" dataKey="box_office_value" stroke="#ed6c02" strokeWidth={2} dot={{ r: 3 }} />
                            <Line name={translate('ara_tiquets_admin.invitations')} type="monotone" dataKey="invitations_value" stroke="#7b1fa2" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{translate('ara_tiquets_admin.attendance_chart')}</Typography>
                <Box sx={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={attendanceData} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" />
                            <YAxis tickFormatter={value => mode === 'percentage' ? `${value}%` : value} />
                            <Tooltip content={chartTooltip(mode, translate)} />
                            <Bar name={translate('ara_tiquets_admin.people')} dataKey="chart_value">
                                {attendanceData.map(item => (
                                    <Cell key={item.source_label} fill={item.source_label === 'attended' ? '#2e7d32' : '#9e9e9e'} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>
                </>
            )}
        </Box>
    );
};

const SessionsFilter = props => {
    const translate = useTranslate();
    const isSuperAdmin = localStorage.getItem('permissions_groups') === 'SIFAC';

    return (
        <Filter {...props}>
            <TextInput
                alwaysOn
                label={translate('common.search')}
                source="q"
                validate={[validateMaxLength(100, translate('common.validation.maxLength'))]}
                resettable
            />
            {isSuperAdmin ? (
                <ReferenceInput
                    alwaysOn
                    label={translate('ara_tiquets_admin.association')}
                    source="associations_id"
                    reference="associations"
                    perPage={1000}
                    sort={{ field: 'name', order: 'ASC' }}
                >
                    <SelectInput optionText="name" optionValue="id" resettable />
                </ReferenceInput>
            ) : null}
        </Filter>
    );
};

const ListActions = ({ onCreateClick, showCreateButton }) => (
    <TopToolbar>
        {showCreateButton ?
        <Button onClick={onCreateClick} label="ara_tiquets_admin.add_event">
            <AddIcon />
        </Button>
        : null}
    </TopToolbar>
);

const EmptyListMessage = ({ message }) => (
    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <Typography>{message}</Typography>
    </Box>
);

const ReservationsTable = ({ rows, columns, detailColumns, detailRender, sortField, sortOrder, onSort }) => {
    const [expandedId, setExpandedId] = React.useState(null);
    const hasDetail = (detailColumns && detailColumns.length > 0) || Boolean(detailRender);

    return (
        <Box sx={{ overflow: 'auto', border: '1px solid #eeeeee', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {hasDetail ? <TableCell sx={{ width: 42 }} /> : null}
                        {columns.map(column => (
                            <TableCell key={column.key} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {column.sortable ? (
                                    <TableSortLabel
                                        active={sortField === column.key}
                                        direction={sortField === column.key ? sortOrder : 'asc'}
                                        onClick={() => onSort && onSort(column.key)}
                                    >
                                        {column.label}
                                    </TableSortLabel>
                                ) : column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(row => (
                        <React.Fragment key={row.id}>
                            <TableRow hover>
                                {hasDetail ? (
                                    <TableCell sx={{ width: 42 }}>
                                        <Button onClick={() => setExpandedId(expandedId === row.id ? null : row.id)} label="">
                                            {expandedId === row.id ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                        </Button>
                                    </TableCell>
                                ) : null}
                                {columns.map(column => (
                                    <TableCell key={column.key} sx={{ whiteSpace: 'nowrap' }}>
                                        {column.render ? column.render(row) : row[column.key]}
                                    </TableCell>
                                ))}
                            </TableRow>
                            {hasDetail ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1} sx={{ p: 0, borderBottom: expandedId === row.id ? undefined : 0 }}>
                                        <Collapse in={expandedId === row.id} timeout="auto" unmountOnExit>
                                            {detailRender ? detailRender(row) : (
                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1.5, p: 2, backgroundColor: '#fafafa' }}>
                                                    {detailColumns.map(column => (
                                                        <Box key={column.key}>
                                                            <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>{column.label}</Typography>
                                                            <Typography variant="body2">{column.render ? column.render(row) : (row[column.key] || '')}</Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};
const escapeHtml = value => String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const exportEmergencyTicketsPdf = (rows, columns, record, translate, notify) => {
    const exportDate = new Date().toLocaleString('ca-ES');
    const title = record ? record.event_name || '' : '';
    const sessionDate = record ? dateTimeLabel(record.date_start) : '';
    const exportColumns = columns.filter(column => column.exportable !== false);
    const headers = [translate('tickets_booking_items.attendance_check')].concat(exportColumns.map(column => column.label));
    const bodyRows = rows.map(row => {
        const cells = ['<span class="checkbox"></span>'].concat(exportColumns.map(column => {
            const value = column.export ? column.export(row) : (column.render ? column.render(row) : row[column.key]);
            return escapeHtml(value);
        }));
        return '<tr>' + cells.map(cell => '<td>' + cell + '</td>').join('') + '</tr>';
    }).join('');
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
        notify('ra.notification.http_error', 'warning');
        return;
    }

    printWindow.document.write(`
        <!doctype html>
        <html>
            <head>
                <title>${escapeHtml(title)}</title>
                <style>
                    body { font-family: Arial, sans-serif; color: #111; margin: 24px; }
                    h1 { font-size: 20px; margin: 0 0 6px; }
                    .meta { font-size: 12px; margin-bottom: 16px; color: #333; }
                    table { width: 100%; border-collapse: collapse; font-size: 11px; }
                    th, td { border: 1px solid #555; padding: 6px; text-align: left; vertical-align: middle; }
                    th { background: #f0f0f0; }
                    .checkbox { display: inline-block; width: 16px; height: 16px; border: 2px solid #111; }
                    @media print { body { margin: 12mm; } }
                </style>
            </head>
            <body>
                <h1>${escapeHtml(title)}</h1>
                <div class="meta">
                    <div>${escapeHtml(translate('tickets_booking_items.session_date'))}: ${escapeHtml(sessionDate)}</div>
                    <div>${escapeHtml(translate('tickets_booking_items.export_date'))}: ${escapeHtml(exportDate)}</div>
                </div>
                <table>
                    <thead><tr>${headers.map(header => '<th>' + escapeHtml(header) + '</th>').join('')}</tr></thead>
                    <tbody>${bodyRows}</tbody>
                </table>
                <script>window.onload = function(){ window.print(); };</script>
            </body>
        </html>
    `);
    printWindow.document.close();
};
const exportRows = (rows, columns, fileName, translate, notify) => {
    const items = rows.map(row => {
        const item = {};
        columns.filter(column => column.exportable !== false).forEach(column => {
            item[column.label] = column.export ? column.export(row) : (column.render ? column.render(row) : row[column.key]);
        });
        return item;
    });

    jsonExport(items, {}, (err, csv) => {
        if (err) {
            notify('ra.notification.http_error', 'warning');
            return;
        }
        downloadCSV('\uFEFF' + csv, fileName);
        notify(translate('common.download_ok'), 'success');
    });
};

const ReservationsDialog = ({ open, record, onClose }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const [tab, setTab] = React.useState(0);
    const [bookings, setBookings] = React.useState([]);
    const [tickets, setTickets] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [ticketSearch, setTicketSearch] = React.useState('');
    const [ticketSort, setTicketSort] = React.useState({ field: 'booking_date_time', order: 'desc' });

    React.useEffect(() => {
        if (!open || !record) return;

        setLoading(true);
        Promise.all([
            dataProvider.getList('tickets_booking', {
                sort: { field: 'id', order: 'DESC' },
                filter: { associations_id: record.associations_id || localStorage.getItem('associations_id'), events_sessions_id: record.id },
                pagination: { page: 1, perPage: 100000 }
            }),
            dataProvider.getList('tickets_booking_items', {
                sort: { field: 'id', order: 'ASC' },
                filter: { associations_id: record.associations_id || localStorage.getItem('associations_id'), events_sessions_id: record.id },
                pagination: { page: 1, perPage: 100000 }
            })
        ]).then(([bookingsResult, ticketsResult]) => {
            setBookings(bookingsResult.data || []);
            setTickets(ticketsResult.data || []);
        }).catch(error => {
            console.error(error);
            notify('ra.notification.http_error', 'warning');
        }).finally(() => setLoading(false));
    }, [open, record, dataProvider, notify]);

    const bookingColumns = [
        { key: 'booking_date_time', label: translate('tickets_booking.booking_date_time'), render: row => dateTimeLabel(row.booking_date_time), export: row => dateTimeLabel(row.booking_date_time) },
        { key: 'reference', label: translate('tickets_booking.reference') },
        { key: 'first_name', label: translate('tickets_booking.first_name') },
        { key: 'last_name_1', label: translate('tickets_booking.last_name_1') },
        { key: 'email', label: translate('tickets_booking.email') },
        { key: 'tickets_count', label: translate('tickets_booking.tickets_count'), render: row => ticketCount(row, tickets), export: row => ticketCount(row, tickets) },
        { key: 'payment_status', label: translate('tickets_booking.payment_status') },
        { key: 'payment_method', label: translate('tickets_booking.payment_method') },
        { key: 'origin', label: translate('tickets_booking.origin'), render: row => originLabel(row), export: row => originLabel(row) },
        {
            key: 'amount_total',
            label: translate('tickets_booking.amount_total'),
            render: row => <PurchasePriceField record={row} />,
            export: row => amountLabel(row.amount_total)
        },
        {
            key: 'pdf_action',
            label: translate('tickets_booking.download_pdf'),
            render: row => bookingPdfUrl(row) ? (
                <IconButton
                    size="small"
                    onClick={event => downloadPrivatePdf(event, bookingPdfUrl(row), `entrades-${row.reference || row.id}.pdf`, notify)}
                    title={translate('tickets_booking.download_pdf')}
                    aria-label={translate('tickets_booking.download_pdf')}
                >
                    <PictureAsPdfIcon fontSize="small" />
                </IconButton>
            ) : null,
            export: () => ''
        },
        {
            key: 'send_ticket_action',
            label: translate('tickets_booking.send_ticket'),
            render: row => (
                <Button onClick={() => notify(translate('tickets_booking.send_ticket_pending'), 'info')} label="" title={translate('tickets_booking.send_ticket')}>
                    <SendIcon />
                </Button>
            ),
            export: () => ''
        },
        { key: 'send_status', label: translate('tickets_booking.send_status'), render: row => statusLabel(row.send_status), export: row => statusLabel(row.send_status) },
        {
            key: 'send_resend_action',
            label: translate('tickets_booking.resend_confirmation'),
            render: row => (
                <Button onClick={() => notify(translate('tickets_booking.resend_confirmation_pending'), 'info')} label="" title={translate('tickets_booking.resend_confirmation')}>
                    <SendIcon />
                </Button>
            ),
            export: () => ''
        },
        { key: 'remember_status', label: translate('tickets_booking.remember_status'), render: row => statusLabel(row.remember_status), export: row => statusLabel(row.remember_status) },
        {
            key: 'remember_resend_action',
            label: translate('tickets_booking.resend_reminder'),
            render: row => (
                <Button onClick={() => notify(translate('tickets_booking.resend_reminder_pending'), 'info')} label="" title={translate('tickets_booking.resend_reminder')}>
                    <SendIcon />
                </Button>
            ),
            export: () => ''
        },
        {
            key: 'invoice_action',
            label: translate('tickets_booking.send_invoice'),
            render: row => (
                <Button onClick={() => notify(translate('tickets_booking.send_invoice_pending'), 'info')} label="" title={translate('tickets_booking.send_invoice')}>
                    <SendIcon />
                </Button>
            ),
            export: () => ''
        }
    ];
    const ticketColumns = [
        { key: 'booking_date_time', label: translate('tickets_booking.booking_date_time'), sortable: true, render: row => dateTimeLabel(row.booking_date_time), export: row => dateTimeLabel(row.booking_date_time) },
        { key: 'ticket_code', label: translate('tickets_booking_items.ticket_code'), sortable: true },
        { key: 'booking_reference', label: translate('tickets_booking_items.booking_reference'), sortable: true },
        { key: 'prices_name', label: translate('tickets_booking_items.prices_name') },
        {
            key: 'price_total',
            label: translate('tickets_booking_items.price_total'),
            render: row => <PurchasePriceField record={row} totalSource="price_total" />,
            export: row => amountLabel(row.price_total)
        },
        { key: 'attendance', label: translate('tickets_booking_items.attendance'), render: row => attendanceLabel(row), export: row => attendanceLabel(row) },
        {
            key: 'ticket_pdf_action',
            label: 'Entrada',
            render: row => ticketPdfUrl(row) ? (
                <IconButton
                    size="small"
                    onClick={event => downloadPrivatePdf(event, ticketPdfUrl(row), `entrada-${row.ticket_code || row.id}.pdf`, notify)}
                    title={translate('tickets_booking_items.download_pdf')}
                    aria-label={translate('tickets_booking_items.download_pdf')}
                >
                    <PictureAsPdfIcon fontSize="small" />
                </IconButton>
            ) : null,
            exportable: false
        }
    ];

    const ticketDetailColumns = [
        { key: 'seat_access', label: translate('tickets_booking_items.seat_access') },
        { key: 'seat_zone', label: translate('tickets_booking_items.seat_zone') },
        { key: 'seat_row', label: translate('tickets_booking_items.seat_row') },
        { key: 'seat_number', label: translate('tickets_booking_items.seat_number') },
        { key: 'seat_label', label: translate('tickets_booking_items.seat_label') },
        { key: 'tickets_booking_subscription_id', label: translate('tickets_booking_items.tickets_booking_subscription_id') },
        { key: 'validated_at', label: translate('tickets_booking_items.validated_at'), render: row => dateTimeLabel(row.validated_at), export: row => dateTimeLabel(row.validated_at) },
        { key: 'validated_for', label: translate('tickets_booking_items.validated_for') },
        { key: 'num_validations', label: translate('tickets_booking_items.num_validations') },
        { key: 'unvalidated_at', label: translate('tickets_booking_items.unvalidated_at'), render: row => dateTimeLabel(row.unvalidated_at), export: row => dateTimeLabel(row.unvalidated_at) },
        { key: 'unvalidated_for', label: translate('tickets_booking_items.unvalidated_for') },
        { key: 'num_unvalidations', label: translate('tickets_booking_items.num_unvalidations') }
    ];

    const bookingsById = new Map(bookings.map(booking => [Number(booking.id), booking]));
    const ticketsWithBooking = tickets.map(ticket => {
        const booking = bookingsById.get(Number(ticket.tickets_booking_id)) || {};
        return {
            ...ticket,
            booking_reference: booking.reference || '',
            booking_date_time: booking.booking_date_time || ''
        };
    });
    const compareTickets = (a, b) => {
        const primary = String(a[ticketSort.field] || '').localeCompare(String(b[ticketSort.field] || ''));
        const secondary = String(a.ticket_code || '').localeCompare(String(b.ticket_code || ''));
        const result = primary || secondary;
        return ticketSort.order === 'asc' ? result : -result;
    };
    const handleTicketSort = field => setTicketSort(current => ({
        field,
        order: current.field === field && current.order === 'asc' ? 'desc' : 'asc'
    }));
    const filteredTickets = (ticketSearch
        ? ticketsWithBooking.filter(ticket => {
            const search = ticketSearch.toLowerCase();
            return String(ticket.ticket_code || '').toLowerCase().includes(search)
                || String(ticket.booking_reference || '').toLowerCase().includes(search);
        })
        : ticketsWithBooking
    ).sort(compareTickets);
    const bookingTickets = booking => ticketsWithBooking.filter(ticket => Number(ticket.tickets_booking_id) === Number(booking.id)).sort(compareTickets);
    const activeRows = tab === 0 ? filteredTickets : bookings;
    const activeColumns = tab === 0 ? ticketColumns : bookingColumns;
    const activeDetailColumns = tab === 0 ? ticketDetailColumns : null;
    const activeDetailRender = tab === 1 ? booking => (
        <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
            <ReservationsTable rows={bookingTickets(booking)} columns={ticketColumns} sortField={ticketSort.field} sortOrder={ticketSort.order} onSort={handleTicketSort} />
        </Box>
    ) : null;

    return (
        <Dialog fullWidth maxWidth="xl" open={open} onClose={onClose}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <span>{record ? sessionTitle(record) : translate('ara_tiquets_admin.view_bookings')}</span>
                <IconButton onClick={onClose} size="small" aria-label={translate('ra.action.close')}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                    <Tabs value={tab} onChange={(event, value) => setTab(value)}>
                        <Tab label={translate('ara_tiquets_admin.tickets_tab') + ' (' + tickets.length + ')'} />
                        <Tab label={translate('ara_tiquets_admin.bookings_tab') + ' (' + bookings.length + ')'} />
                    </Tabs>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={() => exportRows(activeRows, activeColumns, `${tab === 0 ? 'entrades' : 'compres'}_${record ? record.id : ''}`, translate, notify)}
                            label="CSV"
                            disabled={loading || activeRows.length === 0}
                        >
                            <DownloadIcon />
                        </Button>
                        {tab === 0 ? (
                            <Button
                                onClick={() => exportEmergencyTicketsPdf(activeRows, activeColumns, record, translate, notify)}
                                label="PDF"
                                disabled={loading || activeRows.length === 0}
                            >
                                <PictureAsPdfIcon />
                            </Button>
                        ) : null}
                    </Box>
                </Box>
                {tab === 0 ? (
                    <Box sx={{ mb: 2, maxWidth: 360 }}>
                        <MuiTextField
                            fullWidth
                            size="small"
                            value={ticketSearch}
                            onChange={event => setTicketSearch(event.target.value)}
                            label={translate('tickets_booking_items.search_ticket_code')}
                        />
                    </Box>
                ) : null}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
                ) : (
                    <ReservationsTable rows={activeRows} columns={activeColumns} detailColumns={activeDetailColumns} detailRender={activeDetailRender} sortField={tab === 0 ? ticketSort.field : undefined} sortOrder={tab === 0 ? ticketSort.order : undefined} onSort={tab === 0 ? handleTicketSort : undefined} />
                )}
            </DialogContent>
        </Dialog>
    );
};

const RowActionsField = ({ record, onBookingsClick }) => {
    const translate = useTranslate();
    const isSuperAdmin = localStorage.getItem('permissions_groups') === 'SIFAC';
    if (!record) return null;

    return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            {!isSuperAdmin ? (
                <Button
                    onClick={() => onBookingsClick(record)}
                    label=""
                    title={translate('ara_tiquets_admin.view_bookings')}
                >
                    <ReceiptLongIcon />
                </Button>
            ) : null}
            <Button
                component={Link}
                to={`/events_sessions/${record.id}`}
                label=""
                title={translate('ara_tiquets_admin.edit_session')}
            >
                <EditIcon />
            </Button>
            <Button
                component="a"
                href={record.friendly_url ? `${araTiquetsUrl}/events/${record.friendly_url}` : undefined}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!record.friendly_url}
                label=""
                title={translate('ara_tiquets_admin.view_public_event')}
            >
                <OpenInNewIcon />
            </Button>
            <Button
                disabled
                label=""
                title={translate('ara_tiquets_admin.cancel_event')}
            >
                <CancelIcon />
            </Button>
        </Box>
    );
};

const SubscriptionPurchasesDialog = ({ open, record, onClose }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const [purchases, setPurchases] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [search, setSearch] = React.useState('');

    React.useEffect(() => {
        if (!open || !record) return;

        setLoading(true);
        dataProvider.getList('ara-tiquets/profile/subscriptions', {
            sort: { field: 'id', order: 'DESC' },
            filter: {
                events_subscriptions_id: record.id,
                all_sales: true
            },
            pagination: { page: 1, perPage: 100000 }
        }).then(result => {
            setPurchases(result.data || []);
        }).catch(error => {
            console.error(error);
            notify('ra.notification.http_error', 'warning');
        }).finally(() => setLoading(false));
    }, [open, record, dataProvider, notify]);

    React.useEffect(() => {
        if (!open) {
            setPurchases([]);
            setSearch('');
        }
    }, [open]);

    const columns = [
        { key: 'booking_date_time', label: translate('tickets_booking.booking_date_time'), render: row => dateTimeLabel(row.booking_date_time) },
        { key: 'reference', label: translate('tickets_booking.reference') },
        { key: 'first_name', label: translate('tickets_booking.first_name') },
        { key: 'last_name_1', label: translate('tickets_booking.last_name_1') },
        { key: 'email', label: translate('tickets_booking.email') },
        { key: 'price_name', label: translate('ara_tiquets_admin.subscription_price') },
        { key: 'subscription_code', label: translate('ara_tiquets_admin.subscription_code') },
        {
            key: 'price_total',
            label: translate('tickets_booking.amount_total'),
            render: row => <PurchasePriceField record={row} subscriptions totalSource="price_total" />,
            export: row => amountLabel(row.price_total)
        },
        { key: 'box_office_user_email', label: translate('ara_tiquets_admin.box_office_user') }
    ];
    const normalizedSearch = search.trim().toLowerCase();
    const filteredPurchases = normalizedSearch
        ? purchases.filter(row => [
            row.reference,
            row.first_name,
            row.last_name_1,
            row.last_name_2,
            row.email,
            row.subscription_code,
            row.box_office_user_email
        ].some(value => String(value || '').toLowerCase().includes(normalizedSearch)))
        : purchases;

    return (
        <Dialog fullWidth maxWidth="xl" open={open} onClose={onClose}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <span>{translate('ara_tiquets_admin.subscription_purchases')}: {record ? record.name : ''}</span>
                <IconButton onClick={onClose} size="small" aria-label={translate('ra.action.close')}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2, maxWidth: 420 }}>
                    <MuiTextField
                        fullWidth
                        size="small"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        label={translate('ra.action.search')}
                    />
                </Box>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
                ) : (
                    <ReservationsTable rows={filteredPurchases} columns={columns} />
                )}
            </DialogContent>
        </Dialog>
    );
};

const SubscriptionActionsField = ({ record, onPurchasesClick }) => {
    const translate = useTranslate();
    if (!record) return null;

    return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button
                onClick={() => onPurchasesClick(record)}
                label=""
                title={translate('ara_tiquets_admin.view_subscription_purchases')}
            >
                <ReceiptLongIcon />
            </Button>
            <Button
                component={Link}
                to={`/events_subscriptions/${record.id}`}
                label=""
                title={translate('ara_tiquets_admin.edit_subscription')}
            >
                <EditIcon />
            </Button>
            <Button
                component="a"
                href={record.association_friendly_url ? `${araTiquetsUrl}/entitats/${record.association_friendly_url}/subscriptions` : undefined}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!record.association_friendly_url}
                label=""
                title={translate('ara_tiquets_admin.view_public_subscriptions')}
            >
                <OpenInNewIcon />
            </Button>
        </Box>
    );
};

const SubscriptionsSection = props => {
    const translate = useTranslate();
    const [purchasesRecord, setPurchasesRecord] = React.useState(null);

    return (
        <>
            <List
                {...props}
                resource="events_subscriptions"
                basePath="/ara-tiquets"
                title={translate('ara_tiquets_admin.subscriptions_section')}
                actions={null}
                empty={<EmptyListMessage message={translate('ara_tiquets_admin.no_subscriptions')} />}
                filter={{ associations_id: localStorage.getItem('associations_id') }}
                sort={{ field: 'priority', order: 'DESC' }}
                perPage={25}
                bulkActionButtons={false}
            >
                <Datagrid>
                    <TextField source="name" label={translate('common.name')} sortable={false} />
                    <TextField source="events_count" label={translate('events_subscriptions.events_count')} sortable={false} />
                    <BooleanField source="actived" label={translate('common.actived')} sortable={false} />
                    <TextField source="total_capacity" label={translate('events_subscriptions.total_capacity')} sortable={false} />
                    <FunctionField label={translate('ara_tiquets_admin.sales_status')} render={record => <SalesReportField record={record} subscriptions />} />
                    <FunctionField label={translate('ara_tiquets_admin.income_total')} render={record => <IncomeTotalField record={record} subscriptions />} />
                    <FunctionField
                        label={translate('common.actions')}
                        render={record => <SubscriptionActionsField record={record} onPurchasesClick={setPurchasesRecord} />}
                    />
                </Datagrid>
            </List>
            <SubscriptionPurchasesDialog
                open={Boolean(purchasesRecord)}
                record={purchasesRecord}
                onClose={() => setPurchasesRecord(null)}
            />
        </>
    );
};

export const AraTiquetsAdminSessionsList = props => {
    const translate = useTranslate();
    const [section, setSection] = React.useState(0);
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
    const [reservationsRecord, setReservationsRecord] = React.useState(null);
    const isSuperAdmin = localStorage.getItem('permissions_groups') === 'SIFAC';

    const filter = isSuperAdmin ? {} : { associations_id: localStorage.getItem('associations_id') };

    return (
        <>
        <Box sx={{ backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0', mb: 2 }}>
            <Tabs value={section} onChange={(event, value) => setSection(value)}>
                <Tab label={translate('ara_tiquets_admin.events_section')} />
                <Tab label={translate('ara_tiquets_admin.subscriptions_section')} />
            </Tabs>
        </Box>
        {section === 0 ? (
        <List
            {...props}
            resource="ara-tiquets/admin/sessions"
            basePath="/ara-tiquets"
            title={translate('main_menu.ara_tiquets')}
            actions={<ListActions showCreateButton={!isSuperAdmin} onCreateClick={() => setCreateDialogOpen(true)} />}
            empty={<EmptyListMessage message={translate('ara_tiquets_admin.no_ticketing_events')} />}
            filters={<SessionsFilter />}
            filter={filter}
            sort={{ field: 'date_start', order: 'DESC' }}
            perPage={25}
            bulkActionButtons={false}
        >
            <Datagrid expand={<AdminSessionExpand />}>
                <FunctionField
                    source="event_name"
                    label={translate('ara_tiquets_admin.event_name')}
                    render={record => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            {record.image_url ? (
                                <img
                                    src={record.image_url}
                                    className="ara-tiquets-session-image"
                                    title={record.event_name}
                                    alt={record.event_name || ''}
                                />
                            ) : null}
                            <span>{record.event_name}</span>
                        </Box>
                    )}
                />
                <TextField source="session_name" label={translate('ara_tiquets_admin.session_name')} />
                <DateField source="date_start" label={translate('ara_tiquets_admin.session_date')} showTime />
                <FunctionField
                    source="maps_id"
                    label={translate('ara_tiquets_admin.seating_map')}
                    sortable={false}
                    render={record => record.maps_id ? <TicketsMapSvgPreview mapId={record.maps_id} sessionId={record.id} title={sessionTitle(record)} editable /> : null}
                />
                {isSuperAdmin ? <TextField source="associations_name" label={translate('ara_tiquets_admin.association')} /> : null}
                <FunctionField label={translate('ara_tiquets_admin.total_on_sale')} render={record => record.map_status_total || 0} />
                <FunctionField label={translate('ara_tiquets_admin.sales_status')} render={record => <SalesReportField record={record} />} />
                <FunctionField label={translate('ara_tiquets_admin.income_total')} render={record => <IncomeTotalField record={record} />} />
                <FunctionField label={translate('common.actions')} render={record => <RowActionsField record={record} onBookingsClick={setReservationsRecord} />} />
            </Datagrid>
        </List>
        ) : null}
        {section === 1 ? <SubscriptionsSection {...props} /> : null}
        <Dialog
            fullWidth
            maxWidth="lg"
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            disableEnforceFocus
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <span>{translate('ara_tiquets_admin.add_event')}</span>
                <IconButton
                    onClick={() => setCreateDialogOpen(false)}
                    size="small"
                    aria-label={translate('ra.action.close')}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <CreateEventQuickForm
                initialEventType="ticketing"
                onCancel={() => setCreateDialogOpen(false)}
            />
        </Dialog>
        <ReservationsDialog
            open={Boolean(reservationsRecord)}
            record={reservationsRecord}
            onClose={() => setReservationsRecord(null)}
        />
        </>
    );
};
